import React, { useState, useRef } from 'react';
import { MapPin, Mail, Globe, Send, User, Phone, Building, MessageSquare } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import OrangeIcon from './OrangeIcon';
import { CONTACT } from '../mock';

const GF_ACTION = 'https://docs.google.com/forms/d/e/1FAIpQLSesXzM-pT9lKsIk5D6PWJ90b9msF5hOnf9-bzt9hRMHyk7xGA/formResponse';
const IFRAME_NAME = 'google-form-submit-iframe';
const EMPTY_FORM = { name: '', email: '', phone: '', company: '', subject: '', message: '' };

const ContactSection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();

    // Existing validation — unchanged
    if (!form.name || !form.email || !form.phone || !form.subject || !form.message) {
      toast({ title: 'Missing information', description: 'Please fill in all required fields.' });
      return;
    }

    setSubmitting(true);

    // Programmatically submit the hidden native form that targets the iframe.
    // The iframe absorbs Google's redirect response — the user stays on this page.
    if (formRef.current) formRef.current.submit();

    // We cannot read Google's cross-origin response, so we optimistically
    // show success after a short delay (enough for the POST to dispatch).
    setTimeout(() => {
      toast({ title: 'Enquiry sent', description: 'Our team will respond within 24 hours.' });
      setForm(EMPTY_FORM);
      setSubmitting(false);
    }, 1200);
  };

  return (
    <section id="contact" className="relative bg-[#0A1628] py-16 md:py-20 navy-grid">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="reveal-on-scroll">
          <h2 className="font-display font-black text-4xl md:text-6xl leading-tight mt-2">
            REQUEST A <span className="orange-accent">DEMO</span>
          </h2>
          <p className="text-white/75 mt-3 max-w-2xl">Talk to our team about HERTZX™ deployment for your mission requirements.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
          {/* Info panel */}
          <div className="lg:col-span-5 space-y-4">
            <div className="dark-card rounded-xl p-5 reveal-on-scroll">
              <div className="flex items-start gap-3">
                <OrangeIcon name="MapPin" size={44} iconSize={20} />
                <div>
                  <div className="text-[#F5901F] text-xs font-bold tracking-[0.2em]">HEAD OFFICE</div>
                  <div className="text-white/85 text-sm mt-1">{CONTACT.address}</div>
                </div>
              </div>
            </div>
            <div className="dark-card rounded-xl p-5 reveal-on-scroll">
              <div className="flex items-start gap-3">
                <OrangeIcon name="Mail" size={44} iconSize={20} />
                <div>
                  <div className="text-[#F5901F] text-xs font-bold tracking-[0.2em]">EMAIL</div>
                  <a href={`mailto:${CONTACT.email}`} className="text-white/90 text-sm mt-1 hover:text-[#F5901F] transition-colors">{CONTACT.email}</a>
                </div>
              </div>
            </div>
            <div className="dark-card rounded-xl p-5 reveal-on-scroll">
              <div className="flex items-start gap-3">
                <OrangeIcon name="Globe" size={44} iconSize={20} />
                <div>
                  <div className="text-[#F5901F] text-xs font-bold tracking-[0.2em]">WEBSITE</div>
                  <a href={`https://${CONTACT.website}`} target="_blank" rel="noreferrer" className="text-white/90 text-sm mt-1 hover:text-[#F5901F] transition-colors">{CONTACT.website}</a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 dark-card rounded-xl p-6 md:p-8 reveal-on-scroll">
            <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide flex items-center gap-1"><User size={14} /> Name</label>
                <Input name="name" value={form.name} onChange={onChange} placeholder="Your full name" className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide flex items-center gap-1"><Mail size={14} /> Email</label>
                <Input name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.com" className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide flex items-center gap-1"><Phone size={14} /> Phone</label>
                <Input name="phone" value={form.phone} onChange={onChange} placeholder="+91 …" className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide flex items-center gap-1"><Building size={14} /> Company</label>
                <Input name="company" value={form.company} onChange={onChange} placeholder="Your organization" className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide flex items-center gap-1"><MessageSquare size={14} /> Subject</label>
                <Input name="subject" value={form.subject} onChange={onChange} placeholder="How can we help?" className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs text-white/70 font-semibold tracking-wide">Message*</label>
                <Textarea name="message" value={form.message} onChange={onChange} placeholder="Tell us about your mission requirement…" rows={5} className="bg-[#0A1628] border-white/10 text-white focus:ring-[#F5901F]" />
              </div>
              <div className="md:col-span-2 flex items-center justify-end pt-2">
                <Button type="submit" disabled={submitting} className="bg-[#F5901F] text-black hover:bg-[#ffa337] font-bold px-6 py-5">
                  <Send size={16} className="mr-2" />{submitting ? 'Sending…' : 'Send Enquiry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Hidden iframe — absorbs Google's redirect so the page never navigates away */}
      <iframe
        name={IFRAME_NAME}
        title="Google Form submission"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/*
        Hidden native HTML form — targets the iframe above.
        Its input names are the Google Forms entry IDs.
        The visible React-controlled inputs above keep their own `name` attributes
        for onChange handling; this shadow form holds the actual values that get POSTed.
      */}
      <form
        ref={formRef}
        action={GF_ACTION}
        method="POST"
        target={IFRAME_NAME}
        style={{ display: 'none' }}
        aria-hidden="true"
      >
        <input name="entry.416606592" value={form.name}    readOnly />
        <input name="entry.1389490897" value={form.email}   readOnly />
        <input name="entry.754049822"  value={form.phone}   readOnly />
        <input name="entry.1884654017" value={form.company} readOnly />
        <input name="entry.1809625084" value={form.subject} readOnly />
        <input name="entry.709555004"  value={form.message} readOnly />
      </form>

      <Toaster />
    </section>
  );
};

export default ContactSection;
